import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getMessages, getChannels } from '../../services/messaging-api.js';
import { CHANNEL_META } from '../../types/messaging.js';
import type { Message, ChannelConfig, ChannelType } from '../../types/messaging.js';
import { navigateTo } from '../../services/router.js';

@customElement('cp-message-history')
export class CpMessageHistory extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) channelFilter: number | undefined = undefined;

  @state() private _messages: Message[] = [];
  @state() private _channels: ChannelConfig[] = [];
  @state() private _total = 0;
  @state() private _loading = false;
  @state() private _loadingMore = false;
  @state() private _error = '';

  private _limit = 50;

  connectedCallback(): void {
    super.connectedCallback();
    void this._loadInitial();
  }

  private async _loadInitial(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const [messagesRes, channels] = await Promise.all([
        getMessages(this._limit, 0, this.channelFilter),
        getChannels(),
      ]);
      this._messages = messagesRes.messages.reverse();
      this._total = messagesRes.total;
      this._channels = channels;
    } catch (err) {
      this._error = err instanceof Error ? err.message : String(err);
    } finally {
      this._loading = false;
    }
  }

  private async _loadMore(): Promise<void> {
    if (this._loadingMore || this._messages.length >= this._total) return;
    this._loadingMore = true;
    try {
      const res = await getMessages(this._limit, this._messages.length, this.channelFilter);
      this._messages = [...res.messages.reverse(), ...this._messages];
      this._total = res.total;
    } catch (err) {
      this._error = err instanceof Error ? err.message : String(err);
    } finally {
      this._loadingMore = false;
    }
  }

  private async _handleFilterChange(e: Event): Promise<void> {
    const value = (e.target as HTMLSelectElement).value;
    this.channelFilter = value === 'all' ? undefined : Number(value);
    this._messages = [];
    await this._loadInitial();
  }

  private _getChannelIcon(channelConfigId: number): string {
    const channel = this._channels.find(c => c.id === channelConfigId);
    if (!channel) return '';
    return CHANNEL_META[channel.channel_type as ChannelType]?.icon ?? '';
  }

  private _getChannelLabel(channelConfigId: number): string {
    const channel = this._channels.find(c => c.id === channelConfigId);
    if (!channel) return '';
    return CHANNEL_META[channel.channel_type as ChannelType]?.label ?? channel.channel_type;
  }

  private _formatTime(isoString: string): string {
    const d = new Date(isoString);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} ${time}`;
  }

  render() {
    return html`
      <div class="cp-message-history">
        ${this._renderFilter()}
        ${this._loading ? this._renderLoading() : nothing}
        ${!this._loading && this._error ? this._renderError() : nothing}
        ${!this._loading && !this._error && this._messages.length === 0 ? this._renderEmpty() : nothing}
        ${!this._loading && this._messages.length > 0 ? this._renderMessages() : nothing}
      </div>
    `;
  }

  private _renderFilter() {
    return html`
      <div class="cp-message-history__filter">
        <span class="cp-message-history__filter-label">Channel:</span>
        <select
          class="cp-message-history__filter-select"
          @change=${this._handleFilterChange}
        >
          <option value="all">Alle</option>
          ${this._channels.map(ch => html`
            <option
              value=${ch.id}
              ?selected=${this.channelFilter === ch.id}
            >
              ${CHANNEL_META[ch.channel_type as ChannelType]?.icon ?? ''} ${ch.display_name}
            </option>
          `)}
        </select>
      </div>
    `;
  }

  private _renderMessages() {
    const hasMore = this._messages.length < this._total;
    return html`
      ${hasMore ? html`
        <div class="cp-message-history__load-more">
          <button
            class="cp-message-history__load-more-btn"
            ?disabled=${this._loadingMore}
            @click=${this._loadMore}
          >
            ${this._loadingMore ? 'Laden...' : 'Aeltere Nachrichten laden'}
          </button>
        </div>
      ` : nothing}
      <div class="cp-message-history__list">
        ${this._messages.map(msg => this._renderMessage(msg))}
      </div>
    `;
  }

  private _renderMessage(msg: Message) {
    const dirClass = msg.direction === 'outbound'
      ? 'cp-message-history__msg--outbound'
      : 'cp-message-history__msg--inbound';

    return html`
      <div class="cp-message-history__msg ${dirClass}">
        <div class="cp-message-history__msg-bubble">${msg.content}</div>
        <div class="cp-message-history__msg-meta">
          <span class="cp-message-history__msg-channel">${this._getChannelIcon(msg.channel_config_id)}</span>
          <span>${this._getChannelLabel(msg.channel_config_id)}</span>
          <span>${this._formatTime(msg.created_at)}</span>
        </div>
      </div>
    `;
  }

  private _renderEmpty() {
    return html`
      <div class="cp-message-history__empty">
        <div class="cp-message-history__empty-icon">&#x1F4AC;</div>
        <h3 class="cp-message-history__empty-title">Noch keine Nachrichten</h3>
        <p class="cp-message-history__empty-text">Verbinde einen Channel und sende deine erste Nachricht.</p>
        <a class="cp-message-history__empty-link" href="#/settings" @click=${(e: Event) => { e.preventDefault(); navigateTo('/settings'); }}>
          Zu den Channel-Settings
        </a>
      </div>
    `;
  }

  private _renderLoading() {
    return html`
      <div class="cp-message-history__loading">
        ${[1, 2, 3, 4].map((_, i) => html`
          <div class="cp-message-history__skeleton ${i % 2 === 0 ? 'cp-message-history__skeleton--left' : 'cp-message-history__skeleton--right'}"></div>
        `)}
      </div>
    `;
  }

  private _renderError() {
    return html`
      <div class="cp-message-history__empty">
        <div class="cp-message-history__empty-icon">&#9888;</div>
        <h3 class="cp-message-history__empty-title">Fehler beim Laden</h3>
        <p class="cp-message-history__empty-text">${this._error}</p>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-message-history': CpMessageHistory;
  }
}
